import { z } from 'zod'

export const GUEST_RSVP_STATUSES = ['unknown', 'invited', 'confirmed', 'declined', 'maybe'] as const

export const guestRsvpStatusSchema = z.enum(GUEST_RSVP_STATUSES)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const optionalEmail = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.email('Enter a valid email address').optional(),
)

const clearableOptionalEmail = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.email('Enter a valid email address').nullable().optional(),
)

const optionalUuid = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.uuid().optional(),
)

const clearableOptionalUuid = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.uuid().nullable().optional(),
)

export const createGuestGroupSchema = z.object({
  name: z.string().trim().min(1, 'Enter group name').max(120),
  description: optionalText(1000),
})

export const updateGuestGroupSchema = z.object({
  name: z.string().trim().min(1, 'Enter group name').max(120).optional(),
  description: clearableOptionalText(1000),
})

export const createGuestHouseholdSchema = z.object({
  name: z.string().trim().min(1, 'Enter family or household name').max(160),
  groupId: optionalUuid,
  notes: optionalText(1000),
})

export const updateGuestHouseholdSchema = z.object({
  name: z.string().trim().min(1, 'Enter family or household name').max(160).optional(),
  groupId: clearableOptionalUuid,
  notes: clearableOptionalText(1000),
})

export const createGuestSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter first name').max(120),
  lastName: optionalText(120),
  email: optionalEmail,
  phone: optionalText(60),
  groupId: optionalUuid,
  householdId: optionalUuid,
  rsvpStatus: guestRsvpStatusSchema.default('unknown'),
  plusOneAllowed: z.boolean().default(false),
  plusOneName: optionalText(160),
  plusOneRsvpStatus: guestRsvpStatusSchema.default('unknown'),
  needsTransport: z.boolean().default(false),
  transportNote: optionalText(1000),
  needsAccommodation: z.boolean().default(false),
  accommodationNote: optionalText(1000),
  dietaryRequirements: optionalText(1000),
  specialRequirements: optionalText(1000),
  notes: optionalText(2000),
})

export const createGuestPartyMemberSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter first name').max(120),
  lastName: optionalText(120),
  groupId: clearableOptionalUuid,
  rsvpStatus: guestRsvpStatusSchema.default('unknown'),
  dietaryRequirements: optionalText(1000),
  specialRequirements: optionalText(1000),
  notes: optionalText(2000),
})

export const createGuestPartySchema = z.object({
  primaryGuest: createGuestSchema.omit({ groupId: true, householdId: true }),
  groupId: optionalUuid,
  householdId: optionalUuid,
  householdName: optionalText(160),
  householdNotes: optionalText(1000),
  members: z.array(createGuestPartyMemberSchema).max(20).default([]),
})

export const updateGuestSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter first name').max(120).optional(),
  lastName: clearableOptionalText(120),
  email: clearableOptionalEmail,
  phone: clearableOptionalText(60),
  groupId: clearableOptionalUuid,
  householdId: clearableOptionalUuid,
  rsvpStatus: guestRsvpStatusSchema.optional(),
  plusOneAllowed: z.boolean().optional(),
  plusOneName: clearableOptionalText(160),
  plusOneRsvpStatus: guestRsvpStatusSchema.optional(),
  needsTransport: z.boolean().optional(),
  transportNote: clearableOptionalText(1000),
  needsAccommodation: z.boolean().optional(),
  accommodationNote: clearableOptionalText(1000),
  dietaryRequirements: clearableOptionalText(1000),
  specialRequirements: clearableOptionalText(1000),
  notes: clearableOptionalText(2000),
})

export type GuestRsvpStatus = (typeof GUEST_RSVP_STATUSES)[number]
export type CreateGuestGroupInput = z.infer<typeof createGuestGroupSchema>
export type UpdateGuestGroupInput = z.infer<typeof updateGuestGroupSchema>
export type CreateGuestHouseholdInput = z.infer<typeof createGuestHouseholdSchema>
export type UpdateGuestHouseholdInput = z.infer<typeof updateGuestHouseholdSchema>
export type CreateGuestInput = z.infer<typeof createGuestSchema>
export type CreateGuestPartyInput = z.infer<typeof createGuestPartySchema>
export type CreateGuestPartyMemberInput = z.infer<typeof createGuestPartyMemberSchema>
export type UpdateGuestInput = z.infer<typeof updateGuestSchema>
